import { ON_LOGOUT } from 'app/actions/authActions';
import { createReducer, getError } from 'utils/appHelpers';
import { API, graphqlOperation } from 'aws-amplify';
import { getCategoriesData, getSharedProjectsWithMe } from 'graphql/projects/queries';
import { dashboardStats } from 'graphql/dashboard/queries';
import store from 'redux/store';
import { isCacheValid, setCacheTimestamp, CACHE_TTL, incrementCacheHit, incrementCacheMiss } from 'utils/cacheHelpers';

const ON_SHARED_PROJECTS_GET = '@@home/ON_SHARED_PROJECTS_GET';
const ON_CATEGORIES_GET = '@@home/ON_CATEGORIES_GET';
const ON_LIKIEES_TOTAL_GET = '@@home/ON_LIKIEES_TOTAL_GET';
const ON_FOLLOWERS_PROJECTS_GET = '@@home/ON_FOLLOWERS_PROJECTS_GET';
const ON_MOST_PRICE_DROPPED_GET = '@@home/ON_MOST_PRICE_DROPPED_GET';
const ON_RECENT_LIKIEES_GET = '@@home/ON_RECENT_LIKIEES_GET';

const initialData = {
  sharedProjects: [],
  categories: [],
  totalLikiees: 0,
  followersProjects: [],
  mostPriceDropped: [],
  recentLikiees: [],
};

const categoriesHandlers = {
  [ON_SHARED_PROJECTS_GET]: (state, action) => {
    return { ...state, sharedProjects: action.payload };
  },
  [ON_CATEGORIES_GET]: (state, action) => {
    return { ...state, categories: action.payload };
  },
  [ON_LIKIEES_TOTAL_GET]: (state, action) => {
    return { ...state, totalLikiees: action.payload };
  },
  [ON_FOLLOWERS_PROJECTS_GET]: (state, action) => {
    return { ...state, followersProjects: action.payload };
  },
  [ON_MOST_PRICE_DROPPED_GET]: (state, action) => {
    return { ...state, mostPriceDropped: action.payload };
  },
  [ON_RECENT_LIKIEES_GET]: (state, action) => {
    return { ...state, recentLikiees: action.payload };
  },
  [ON_LOGOUT]: () => initialData,
};

export const onSetSharedProjects = payload => ({ type: ON_SHARED_PROJECTS_GET, payload });
export const onSetCategories = payload => ({ type: ON_CATEGORIES_GET, payload });
export const onSetLikieesTotal = payload => ({ type: ON_LIKIEES_TOTAL_GET, payload });
export const onSetFollowersProjects = payload => ({ type: ON_FOLLOWERS_PROJECTS_GET, payload });
export const onSetMostPriceDropped = payload => ({ type: ON_MOST_PRICE_DROPPED_GET, payload });
export const onSetRecentLikiees = payload => ({ type: ON_RECENT_LIKIEES_GET, payload });

export const getSharedProjects = async (force = false) => {
  try {
    const state = store.getState();
    const hasData = state.home?.sharedProjects?.length > 0;

    if (!force && hasData && isCacheValid('shared_projects', CACHE_TTL.SHARED_PROJECTS)) {
      incrementCacheHit();
      return;
    }

    incrementCacheMiss();

    const body = { limit: 1000 };
    const { data } = await API.graphql(graphqlOperation(getSharedProjectsWithMe, body));
    store.dispatch(onSetSharedProjects(data.getSharedProjectsWithMe.items));
    setCacheTimestamp('shared_projects');
  } catch (err) {
    throw new Error(getError(err));
  }
};

export const getCategories = async (onSuccess, force = false) => {
  try {
    const state = store.getState();
    const hasData = state.home?.categories?.length > 0;
    if (!force && hasData && isCacheValid('categories', CACHE_TTL.CATEGORIES)) {
      incrementCacheHit();
      onSuccess && onSuccess(true);
      return;
    }
    incrementCacheMiss();
    const { data } = await API.graphql(graphqlOperation(getCategoriesData));
    store.dispatch(onSetCategories(data.getCategories));
    setCacheTimestamp('categories');
    onSuccess && onSuccess(!!data.getCategories?.length);
  } catch (err) {
    throw new Error(getError(err));
  }
};
export const getDashboardStats = async (force = false) => {
  try {
    const state = store.getState();
    const hasData = state.home?.totalLikiees !== undefined;
    if (!force && hasData && isCacheValid('dashboard_stats', CACHE_TTL.DASHBOARD_STATS)) {
      incrementCacheHit();
      return;
    }
    incrementCacheMiss();
    const { data } = await API.graphql(graphqlOperation(dashboardStats));
    store.dispatch(onSetFollowersProjects(data.dashboardStats.followersProjects));
    store.dispatch(onSetMostPriceDropped(data.dashboardStats.mostPriceDropped));
    store.dispatch(onSetRecentLikiees(data.dashboardStats.recentLikiees));
    store.dispatch(onSetSharedProjects(data.dashboardStats.sharedProjectsWithYou));
    store.dispatch(onSetLikieesTotal(data.dashboardStats.likieesTotal));
    setCacheTimestamp('dashboard_stats');
  } catch (err) {
    throw new Error(getError(err));
  }
};
// Add force refresh functions
export const refreshSharedProjects = () => getSharedProjects(true);
export const refreshCategories = onSuccess => getCategories(onSuccess, true);
export const refreshDashboardStats = () => getDashboardStats(true);
export const homeDataReducer = createReducer(initialData, categoriesHandlers);
